import { EventAggregator } from "aurelia-event-aggregator";
import {
	autoinject,
	bindable,
	computedFrom,
	Disposable
} from "aurelia-framework";
import { ScopeType } from "../../scopes/scope-type-service";
import { includes, sortBy } from "lodash";
import { ReportedServiceDataItemService } from "../../customers/reported-service-data-item-service";
import { AttachedDataItem } from "../../reported-service-data-items/reported-service-data-item-models";

@autoinject
export class ReportedDataItemTags {

	@bindable reportedDataItem: AttachedDataItem;
	@bindable availableScopeTypes: ScopeType[];
	@bindable customerId: string;
	@bindable onTagsChanged: (item: any) => void;
	@bindable getUsedScopetypesForMeasure: (measureId: any) => string[];

	addibleScopeTypes: ScopeType[];
	selectListOpen = false;
	onBodyClick: (Event) => void;
	dropdownOpenedSubscriber: Disposable;

	dropDownContents: HTMLElement;

	@computedFrom("reportedDataItem", "reportedDataItem.scopeTypeIds")
	get selectedScopeTypes(): ScopeType[] {
		if (!this.reportedDataItem) {
			return null;
		}
		return sortBy(
			this.availableScopeTypes.filter(st =>
				includes(this.reportedDataItem.scopeTypeIds, st.id)
			),
			st => st.sortOrder
		);
	}

	constructor(
		private reportedServiceDataItemService: ReportedServiceDataItemService,
		private eventAggregator: EventAggregator
	) {
		this.onBodyClick = () => {
			this.close();
		};
	}

	attached() {
		document.addEventListener("click", this.onBodyClick);

		this.dropdownOpenedSubscriber = this.eventAggregator.subscribe(
			"dropdownOpened",
			(viewModel: any) => {
				if (viewModel !== this) {
					this.close();
				}
			}
		);
	}

	detached() {
		document.removeEventListener("click", this.onBodyClick);
		this.dropdownOpenedSubscriber.dispose();
	}

	refreshAddibleTags() {
		this.addibleScopeTypes = this.availableScopeTypes.filter(
			st => !includes(this.reportedDataItem.scopeTypeIds, st.id)
				&& !includes(this.getUsedScopetypesForMeasure({ measureId: this.reportedDataItem.dataItemId }), st.id)
		);
	}

	onToggleClick(event: Event) {
		event.stopPropagation();
		this.toggleSelectList();

		if (this.selectListOpen) {
			this.dropDownContents.classList.remove("reverse");
			const rect = this.dropDownContents.getBoundingClientRect();
			if (rect.top > document.documentElement.clientHeight / 2) {
				this.dropDownContents.classList.add("reverse");
			}
			this.eventAggregator.publish("dropdownOpened", this);
		}
	}

	handleToggle(e: KeyboardEvent) {
		e.stopPropagation();
		if (e.code === "Space" || e.code === "Enter") {
			this.onToggleClick(e);
		}
	}

	toggleSelectList() {

		if (!this.selectListOpen) {
			this.refreshAddibleTags();
		}

		this.selectListOpen = !this.selectListOpen;
	}

	close() {
		this.selectListOpen = false;
	}

	addScopeType(scopeType: ScopeType) {
		this.close();

		if (scopeType == null) {
			return;
		}

		this.reportedServiceDataItemService
			.addScopeTypeId(
				this.reportedDataItem,
				this.customerId,
				scopeType.id
			)
			.then(response => {
				if (response.ok) {
					let newValue = this.reportedDataItem.scopeTypeIds
						? this.reportedDataItem.scopeTypeIds.slice(0)
						: [];
					newValue.push(scopeType.id);
					this.reportedDataItem.scopeTypeIds = newValue; // to trigger change

					this.refreshAddibleTags();

					if (this.onTagsChanged) {
						this.onTagsChanged({ item: this.reportedDataItem });
					}
				}
			});
	}

	removeScopeType(scopeType: ScopeType) {
		const previousScopeTypeIds = this.reportedDataItem.scopeTypeIds;

		let newValue = this.reportedDataItem.scopeTypeIds.filter(
			st => st !== scopeType.id
		);
		this.reportedDataItem.scopeTypeIds = newValue; // to trigger change

		this.refreshAddibleTags();

		this.reportedServiceDataItemService
			.removeScopeTypeId(
				this.reportedDataItem,
				this.customerId,
				scopeType.id
			)
			.then(response => {
				if (response.ok) {
					if (this.onTagsChanged) {
						this.onTagsChanged({ item: this.reportedDataItem });
					}
				} else {
					this.reportedDataItem.scopeTypeIds = previousScopeTypeIds;
					this.refreshAddibleTags();
				}
			});
	}

	handleRemove(e: KeyboardEvent, scopeType: ScopeType) {
		e.stopPropagation();
		if (e.code === "Space" || e.code === "Enter") {
			this.removeScopeType(scopeType);
		}
	}
}
